import type { ContractContext } from './context';
import type { ProductModel } from './product-model';
import type { ConfigurationState, ValidationIssue } from './configuration-state';
import type { ConfigAction } from './actions';
import type { ConfigEvent, SelectionIntentPayload } from './events';
import type { VisualTarget } from './mappings';
import type { PlayerCapabilities } from './capabilities';
import type { ContractError } from './errors';

export interface PlayerInitPayload {
  context: ContractContext;
  productModel: ProductModel;
  initialConfiguration: ConfigurationState;
  capabilitiesRequested?: Partial<PlayerCapabilities>;
}

export interface PlayerApplyResult {
  accepted: boolean;
  pending?: boolean;
  issues?: ValidationIssue[];
}

export interface SnapshotOptions {
  width?: number;
  height?: number;
  mimeType?: 'image/png' | 'image/jpeg';
  cameraId?: string;
}

export interface SnapshotResult {
  snapshotId: string;
  url: string;
}

export interface PlayerAdapter {
  init(payload: PlayerInitPayload): Promise<{ capabilities: PlayerCapabilities }>;
  applyConfiguration(state: ConfigurationState): Promise<PlayerApplyResult>;
  applyDelta(actions: ConfigAction[]): Promise<PlayerApplyResult>;
  highlightTarget(target: VisualTarget): void;
  setView(view: { cameraId?: string; animationId?: string }): void;
  requestSnapshot(options: SnapshotOptions): Promise<SnapshotResult>;
  destroy(): void;
}

export interface PlayerEventBridge {
  onReady(event: ConfigEvent): void;
  onSceneLoaded(event: ConfigEvent): void;
  onInteraction(event: ConfigEvent<SelectionIntentPayload>): void;
  onVisualStateChanged(event: ConfigEvent): void;
  onError(event: ConfigEvent<ContractError>): void;
}
